import { z } from "zod";
import { appearanceSchema } from "./appearance.js";

import { millimeterSchema, moneyFenSchema, positiveIntSchema, uuidSchema } from "./primitives.js";

/**
 * 商品目录的传输结构。
 *
 * **一个 SKU 就是一档真实可售规格**：同一石种的不同直径是两个 SKU，不是同一件商品的两个参数。
 * 设计里存的是 skuId（见 design-core 的 `REPLACE_SKU`），因此换规格就是换商品，
 * 用户不可能切到不存在的尺寸。
 *
 * 目录是价格与可售性的唯一来源：客户端展示的金额只用于即时估算，
 * 真正下单的价格由服务端按同一份目录重新计算（见架构方案 6.1）。
 */

/** 组件形态。只决定画法，不参与成环计算。 */
export const beadKindSchema = z.enum(["round", "spacer", "irregular", "pendant", "block"]);

export type BeadKind = z.infer<typeof beadKindSchema>;

/** 3D 展示的表面材质模板；决定近似的金属度、粗糙度和透明混合。 */
export const beadMaterialProfileSchema = z.enum([
  "polished_stone",
  "matte_stone",
  "translucent_stone",
  "metal",
  "wood",
]);

export type BeadMaterialProfile = z.infer<typeof beadMaterialProfileSchema>;

/** 二维无照片时使用的占位色，同时作为 3D 材质基色；十六进制小写。 */
export const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-f]{6}$/, { error: "Color must be a lowercase hex value like #a1b2c3." });

/**
 * 一档可售规格（SKU）。
 *
 * 两个尺寸的区别（架构方案 4.2）：
 * - `stringPathMm` 占线长，是这颗组件在绳上占多少毫米，**唯一影响成环尺寸**的量；
 * - `diameterMm` 外形尺寸，只影响绘制与视野，不参与成环计算。
 *
 * 圆珠两者相等（都等于直径）；隔片的占线长是厚度而不是盘径；随型件占线长是两孔距离。
 */
export const catalogSpecSchema = z.object({
  skuId: uuidSchema,
  appearance: appearanceSchema.nullable().optional(),
  specVersion: positiveIntSchema,
  stringPathMm: millimeterSchema.positive(),
  diameterMm: millimeterSchema.positive(),
  /** 单价，整数分。 */
  priceFen: moneyFenSchema.nonnegative(),
  /** 可售库存。为 0 时仍可展示，但不可下单。 */
  stock: z.int().nonnegative(),
});

export type CatalogSpecPayload = z.infer<typeof catalogSpecSchema>;

/** 一个石种，连同它全部可售规格。照片按石种共用一张。 */
export const catalogStoneSchema = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  category: z.string().min(1).max(32),
  kind: beadKindSchema,
  /** 商品实拍照片用于二维卡片；3D 材质由该模板单独控制。旧商品可暂为空。 */
  showcaseMaterial: beadMaterialProfileSchema.nullable(),
  color: hexColorSchema,
  /**
   * 石种照片地址，形如 `/media/ab/abcdef….jpg`。**相对路径**：客户端与接口基址拼接后使用，
   * 这样同一份数据在本地、测试与生产都能用，将来换 CDN 也只改客户端基址。
   * 没有素材时为 null，客户端回退占位画法，不能因此报错。
   */
  photoPath: z.string().min(1).nullable(),
  /** 可售规格，按尺寸升序，至少一档。 */
  specs: z.array(catalogSpecSchema).min(1),
});

export type CatalogStonePayload = z.infer<typeof catalogStoneSchema>;

/**
 * 小程序拉取的整份可售目录。只包含上架石种的上架规格。
 */
export const catalogResponseSchema = z.object({
  /** 目录版本，任何目录写操作都会让它递增；客户端据此判断本地缓存是否还新。 */
  revision: positiveIntSchema,
  /** 分类名，按后台排序。 */
  categories: z.array(z.string().min(1).max(32)),
  stones: z.array(catalogStoneSchema),
});

export type CatalogResponsePayload = z.infer<typeof catalogResponseSchema>;

/** 分类。后台维护，小程序只读。 */
export const adminCategorySchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(32),
  sortOrder: z.int(),
});

export type AdminCategoryPayload = z.infer<typeof adminCategorySchema>;
